<template>
    <div class="pt-8">
        <h2 class="text-base font-medium text-gray-700 mb-1 text-center">Collection</h2>
        <div class="bg-gray-50 border border-gray-200 rounded-xl p-4 pr-3">
            <table class="w-full table-fixed">
                <thead>
                <tr>
                    <th style="width: 40%"></th>
                    <th style="width: 19%"></th>
                    <th style="width: 41%"></th>
                </tr>
                </thead>
                <tbody>
                <td colspan="3" class="pt-0">
                    <div class="relative">
                        <div class="border-t border-gray-200 my-3"></div>
                        <div class="absolute inset-0 flex items-center justify-center">
                            <div class="text-xs px-2 py-1 bg-gray-50 text-gray-400 font-semibold">
                                Breakdown
                            </div>
                        </div>
                    </div>
                </td>
                <tr>
                    <td class="text-sm" colspan="2">Materials</td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">
                                {{ calculateMaterialTotal().toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}
                            </div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td class="text-sm" colspan="2">Labour</td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">
                                {{ calculateLabourTotal().toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}
                            </div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td class="text-sm" colspan="2">Fixed Costs</td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">
                                {{ calculateFixedCostsTotal().toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}
                            </div>
                        </div>
                    </td>
                </tr>
                <td colspan="3" class="pt-3">
                    <div class="relative">
                        <div class="border-t border-gray-200 my-3"></div>
                        <div class="absolute inset-0 flex items-center justify-center">
                            <div class="text-xs px-2 py-1 bg-gray-50 text-gray-400 font-semibold">
                                Sections
                            </div>
                        </div>
                    </div>
                </td>
                <tr v-for="(section,index) in nonOptionalSections()" :key="`nonoptional`+index">
                    <td class="text-gray-600 text-sm" colspan="2">{{ section.title }}</td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <ui-dropdown placement="bottom-start" menu-class="p-3 w-56 flex" class="flex w-full"
                                     trigger-class="flex w-full">>
                            <template #default="{ open, close }">
                                <div class="flex items-center justify-between w-full" @mouseover="open"
                                     @mouseleave="close">
                                    <div>£</div>
                                    <div class="pr-1">{{
                                            calculateSectionTotal(section).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                        }}
                                    </div>
                                </div>
                            </template>
                            <template #menu>
                                <table class="w-full">
                                    <tbody>
                                    <tr>
                                        <td class="text-gray-600 text-sm">Materials</td>
                                        <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                                            {{
                                                '£' + calculateMaterialTotal(section).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                            }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-gray-600 text-sm">Labour</td>
                                        <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                                            {{
                                                '£' + calculateLabourTotal(section).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                            }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-gray-600 text-sm">Fixed Costs</td>
                                        <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                                            {{
                                                '£' + calculateFixedCostsTotal(section).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                            }}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </template>
                        </ui-dropdown>
                    </td>
                </tr>
                <tr>
                    <td class="text-gray-400 italic text-sm" colspan="2">Sections Total</td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">{{
                                    calculateAllSectionTotals().toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                }}
                            </div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td colspan="3" class="pt-3">
                        <div class="relative">
                            <div class="border-t border-gray-200 my-3"></div>
                            <div class="absolute inset-0 flex items-center justify-center">
                                <div class="text-xs px-2 py-1 bg-gray-50 text-gray-400 font-semibold">
                                    Total
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td class="text-gray-600 text-sm" colspan="2">Cost Total</td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">{{
                                    (calculateCostTotal()).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                }}
                            </div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td class="text-gray-600 text-sm" colspan="2">Overheads
                        <div class="inline-block text-gray-400 italic text-xs">({{
                                Math.round((calculateCostTotal() ? calculateOverheadTotal() / calculateCostTotal() : 0) * 100 * 100) / 100
                            }}%)
                        </div>
                    </td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">
                                {{ (calculateOverheadTotal()).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}
                            </div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td class="text-gray-600 text-sm" colspan="2">Profit
                        <div class="inline-block text-gray-400 italic text-xs">({{
                                Math.round((calculateCostTotal() ? calculateProfitTotal() / calculateCostTotal() : 0) * 100 * 100) / 100
                            }}%)
                        </div>
                    </td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">{{
                                    (calculateProfitTotal()).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                }}
                            </div>
                        </div>
                    </td>
                </tr>
                <tr v-if="calculateItemDiscountTotal()">
                    <td class="text-gray-600 text-sm" colspan="2">Discounts</td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">-{{
                                    (calculateItemDiscountTotal()).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                }}
                            </div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td class="text-gray-600 text-sm" colspan="2">Scope of Works Total</td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">{{
                                    (calculateTotal()).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                }}
                            </div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td class="text-gray-600 text-sm" colspan="2">Adjustment
                        <div class="inline-block text-gray-400 italic text-xs">
                            ({{ Math.round(estimate.adjustment * 100) / 100 }}%)
                        </div>
                    </td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div>
                                <ui-input :disabled="estimate.readonly" v-model="adjustment" class="pl-1.5"
                                          @focus="changeAdjustment"
                                          ref="adjustmentChanger"
                                          field="py-0.5 text-right px-1"
                                          placeholder="0.00"></ui-input>
                            </div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td class="text-gray-600 text-sm" colspan="2">MCD
                        <div class="inline-block text-gray-400 italic text-xs">({{ estimate.mcd }}%)</div>
                    </td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">-{{
                                    (calculateMCDTotal()).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                }}
                            </div>
                        </div>
                    </td>
                </tr>
                <tr v-if="estimate.preliminaries.length > 0">
                    <td colspan="3" class="pt-0">
                        <div class="relative">
                            <div class="border-t border-gray-200 my-3"></div>
                        </div>
                    </td>
                </tr>
                <tr v-if="estimate.preliminaries.length > 0">
                    <td class="text-gray-600 text-sm" colspan="2">Preliminaries</td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">{{
                                    (calculatePrelimTotal()).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                }}
                            </div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td colspan="3" class="pt-0">
                        <div class="relative">
                            <div class="border-t border-gray-200 my-3"></div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td class="text-gray-600 text-sm font-bold" colspan="2">Grand Total</td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-bold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">{{
                                    (calculateGrandTotal()).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                }}
                            </div>
                        </div>
                    </td>
                </tr>
                <tr>
                    <td class="text-gray-600 text-sm" colspan="2">Effective OHP
                        <div class="inline-block text-gray-400 italic text-xs">({{
                                Math.round((calculateCostTotal() ? (calculateOHPTotal() / calculateCostTotal()) : 0) * 10000) / 100
                            }}%)
                        </div>
                    </td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <div class="flex items-center justify-between">
                            <div>£</div>
                            <div class="pr-1">{{
                                    calculateOHPTotal().toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                }}
                            </div>
                        </div>
                    </td>
                </tr>
                <tr v-if="optionalSections().length > 0">
                    <td colspan="3" class="pt-3">
                        <div class="relative">
                            <div class="border-t border-gray-200 my-3"></div>
                            <div class="absolute inset-0 flex items-center justify-center">
                                <div class="text-xs px-2 py-1 bg-gray-50 text-gray-400 font-semibold">
                                    Optional sections
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
                <tr v-for="(section,index) in optionalSections()" :key="`optional`+index">
                    <td class="text-gray-600 text-sm" colspan="2">{{ section.title }}</td>
                    <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                        <ui-dropdown placement="bottom-start" menu-class="p-3 w-56 flex" class="flex w-full"
                                     trigger-class="flex w-full">>
                            <template #default="{ open, close }">
                                <div class="flex items-center justify-between w-full" @mouseover="open"
                                     @mouseleave="close">
                                    <div>£</div>
                                    <div class="pr-1">{{
                                            calculateSectionTotal(section).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                        }}
                                    </div>
                                </div>
                            </template>
                            <template #menu>
                                <table class="w-full">
                                    <tbody>
                                    <tr>
                                        <td class="text-gray-600 text-sm">Materials</td>
                                        <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                                            {{
                                                '£' + calculateMaterialTotal(section).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                            }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-gray-600 text-sm">Labour</td>
                                        <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                                            {{
                                                '£' + calculateLabourTotal(section).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                            }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-gray-600 text-sm">Fixed Costs</td>
                                        <td class="text-right text-sm font-open-sans tabular-nums font-semibold">
                                            {{
                                                '£' + calculateFixedCostsTotal(section).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                            }}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </template>
                        </ui-dropdown>
                    </td>
                </tr>
                <tr v-if="optionalSections().length > 0">
                    <td colspan="3">
                        <div class="pt-3 text-xs bg-gray-50 text-gray-400 italic">
                            Options are displayed before any MCD and estimate adjustment and are excluded from the grand
                            total above.
                        </div>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>

import {Estimate} from "../../../models";
import {isNull} from "lodash";
import {resolveNumericText} from "../../../utils";
import UiInput from "../../ui/UiInput.vue";

export default {
    components: {UiInput},
    props: {
        estimate: Estimate,
    },
    watch: {
        estimate: {
            handler(newValue, oldValue) {
                if (!this.editingAdjustment) {
                    this.adjustment = this.calculateAdjustmentTotal() ? '-' + this.calculateAdjustmentTotal().toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : '0.00';
                }
            },
            deep: true,
        },
    },
    data() {
        return {
            adjustment: this.calculateAdjustmentTotal() ? '-' + this.calculateAdjustmentTotal().toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : '0.00',
            editingAdjustment: false,
            resolveNumericText,
        }
    },
    methods: {
        updateAdjustment(o) {
            this.editingAdjustment = true;
            this.estimate.adjustment = this.calculateTotal() ? Math.min((this.resolveNumericText(o) / this.calculateTotal() * 100), 100) : 0;
            this.estimate.update()
        },
        changeAdjustment(o) {
            this.$refs['adjustmentChanger'].$refs['input'].blur();
            if (this.estimate.readonly) return;
            // use a MODEL instead
            this.$modals.open('estimate-adjustment-change', {
                estimate: this.estimate,
                handle: (data) => {
                    this.editingAdjustment = false
                    this.estimate.adjustment = data.percent
                    this.estimate.adjustmentComment = data.comment
                    this.estimate.addSnapshot()
                    this.estimate.update()
                }
            })

        },
        isNumeric(num) {
            return (typeof (num) === 'number' || typeof (num) === "string" && num.trim() !== '') && !isNaN(num);
        },
        asNumber(value, min = null, max = null) {
            let num = parseFloat(value.toString().replace(/[^.\d]/g, ''));
            num = this.isNumeric(num) ? num : 0;
            if (!isNull(min))
                num = Math.max(num, min);
            if (!isNull(max))
                num = Math.min(num, max);
            return num;
        },
        calculateSectionTotal(section) {
            return section.tasks.reduce((a, task) => a + task.items.reduce((a, item) => a + (item.buildTotalMarkedUp - item.discount), 0), 0)
        },
        calculateAllSectionTotals() {
            let total = 0;
            this.nonOptionalSections().forEach((i) => total += this.calculateSectionTotal(i));
            return total;
        },
        optionalSections() {
            return this.estimate.scopeOfWorks.filter(section => section.optional)
        },
        nonOptionalSections() {
            return this.estimate.scopeOfWorks.filter(section => !section.optional)
        },
        calculateMaterialTotal(section = false) {
            let sections
            if (!section) {
                sections = this.nonOptionalSections().filter((i) => section === false || i === section)
            } else {
                sections = Array(section)
            }
            return sections.reduce((a, section) => a + section.tasks.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.materialBuildTotal ? item.materialBuildTotal : 0), 0)
                ), 0
            ), 0)
        },
        calculateLabourTotal(section = false) {
            let sections
            if (!section) {
                sections = this.nonOptionalSections().filter((i) => section === false || i === section)
            } else {
                sections = Array(section)
            }
            return sections.reduce((a, section) => a + section.tasks.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.labourBuildTotal ? item.labourBuildTotal : 0), 0)
                ), 0
            ), 0)
        },
        calculateFixedCostsTotal(section = false) {
            let sections
            if (!section) {
                sections = this.nonOptionalSections().filter((i) => section === false || i === section)
            } else {
                sections = Array(section)
            }
            return sections.reduce((a, section) => a + section.tasks.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.fixedCostBuildTotal ? item.fixedCostBuildTotal : 0), 0)
                ), 0
            ), 0)
        },
        calculateCostTotal() {
            return this.nonOptionalSections().reduce((a, section) => a + section.tasks.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.buildTotal ? item.buildTotal : item.buildTotalMarkedUp - item.profit - item.overheads), 0)
                ), 0
            ), 0)
        },
        calculateItemDiscountTotal() {
            return this.nonOptionalSections().reduce((a, section) => a + section.tasks.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.discount ? item.discount : 0), 0)
                ), 0
            ), 0)
        },
        calculateTotal() {
            return this.nonOptionalSections().reduce((a, section) => a + section.tasks.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.total ? item.total : 0), 0)
                ), 0
            ), 0)
        },
        calculateGrandTotal() {
            return this.nonOptionalSections().reduce((a, section) => a + section.tasks.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.finalTotal ? item.finalTotal : 0), 0)
                ), 0
            ), 0) + this.calculatePrelimTotal();
        },
        calculateOHPTotal() {
            return this.nonOptionalSections().reduce((a, section) => a + section.tasks.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.effectiveOHP ? item.effectiveOHP : 0), 0)
                ), 0
            ), 0)
        },
        calculateAdjustmentTotal() {
            return this.nonOptionalSections().reduce((a, section) => a + section.tasks.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.adjustment ? item.adjustment : 0), 0)
                ), 0
            ), 0)
        },
        calculateMCDTotal() {
            return this.nonOptionalSections().reduce((a, section) => a + section.tasks.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.mcd ? item.mcd : 0), 0)
                ), 0
            ), 0)
        },
        calculatePrelimTotal() {
            return this.estimate.preliminaries.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.total ? item.total : 0), 0)
                ), 0
            )
        },
        calculateOverheadTotal() {
            return this.nonOptionalSections().reduce((a, section) => a + section.tasks.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.overheads ? item.overheads : 0), 0)
                ), 0
            ), 0)
        },
        calculateProfitTotal() {
            return this.nonOptionalSections().reduce((a, section) => a + section.tasks.reduce(
                (a, task) => a + (task.items.reduce(
                        (a, item) => a + (item.profit ? item.profit : 0), 0)
                ), 0
            ), 0)
        },
    },
}
</script>
